import { isAssistantView } from '@/features/assistants/assistant-views';
import { CORE_PANEL_OWNER } from '@/features/plugins/core-panels';
import type { KeyboardShortcut } from '@/lib/platform';
import { currentShortcut, panelShortcutId } from '@/lib/settings-page/keyboard-shortcuts';
import type { SidePanelItem, SidePanelView } from '@/lib/side-panel/side-panel-items';

/** A tool's keys: the user's (Settings › Keyboard shortcuts), else its own. */
function panelKeys(item: SidePanelItem): KeyboardShortcut | undefined {
  if (item.shortcut === undefined) return undefined;
  return currentShortcut(panelShortcutId(item.id)) ?? item.shortcut;
}

/** One launchable entry of the "New tab" page (D9). */
export type PanelToolEntry = {
  key: string;
  item: SidePanelItem;
  label: string;
  shortcut: KeyboardShortcut | undefined;
  /** Adds another instance of a multi-instance tool instead of selecting its view. */
  newInstance: boolean;
  /** Already open in the panel (picking it shows its tab). */
  open: boolean;
  /**
   * `tool`: the panel's tools, in their fixed order; `app`: the apps of the conversation;
   * `more`: the other views, found by the launcher's search only.
   */
  group: 'tool' | 'app' | 'more';
  /** An app's own tab, offered even once open (picking it then selects that tab). */
  app?: { open: boolean };
};

/** The order of the tools (D9): what works on code first, then what runs. */
const TOOL_ORDER = [
  'review',
  'files',
  'browser',
  'terminal',
  'pull-request',
  'plan',
  'ports',
  'computer',
  'desktop',
] as const;
type ToolSlot = (typeof TOOL_ORDER)[number];

/** Tools that only make sense with code: a conversation without a repository has none (D5c). */
const CODE_TOOLS: readonly ToolSlot[] = ['review', 'pull-request'];

/**
 * What a new tab can open, grouped as the launcher shows them: the tools in their fixed order
 * (Review, Files, Browser, Terminal, Pull request, Plan, Ports, Computer, Desktop), the apps, then the rest
 * (Library, Side chat, Live…) for its search. Open tools stay offered and are marked open; a
 * multi-instance tool (Browser, Terminal) adds an instance once open.
 */
export function panelToolEntries({
  items,
  openViews,
  browserSessionView,
  terminalSessionView,
  cloud = false,
  repository = true,
  shell = true,
  localComputer = false,
  localPorts = false,
  hiddenViews = [],
}: {
  /** The conversation's workspace is on this computer, whose servers Ports lists. */
  localPorts?: boolean;
  /** The app runs on this Mac (Crowdex Desktop, local): Computer shows what the agents may use. */
  localComputer?: boolean;
  items: readonly SidePanelItem[];
  openViews: readonly SidePanelView[];
  browserSessionView?: SidePanelView | undefined;
  terminalSessionView?: SidePanelView | undefined;
  /** Whether the conversation runs in a cloud sandbox, whose ports and desktop can be shown. */
  cloud?: boolean;
  /** Whether the conversation works on a repository (Review and Pull request need one). */
  repository?: boolean;
  /**
   * Whether the conversation runs somewhere with a shell (its folder on this computer, its Dossier
   * included, or its cloud machine): Terminal opens there, repository or not.
   */
  shell?: boolean;
  /** Tools this conversation cannot use. */
  hiddenViews?: readonly SidePanelView[];
}): PanelToolEntry[] {
  const slotOf = (item: SidePanelItem): ToolSlot | null => {
    if (item.id === browserSessionView) return 'browser';
    if (item.id === terminalSessionView) return 'terminal';
    return (TOOL_ORDER as readonly string[]).includes(item.id) ? (item.id as ToolSlot) : null;
  };
  const entries = items.flatMap((item): PanelToolEntry[] => {
    // A project's tabs open with its conversation (`features/assistants`), not from here.
    if (
      hiddenViews.includes(item.id) ||
      item.id === 'new-tab' ||
      item.id === 'subagent' ||
      isAssistantView(item.id)
    )
      return [];
    const open = openViews.includes(item.id);
    const base = { key: item.id, item, shortcut: panelKeys(item), open };
    const slot = slotOf(item);
    if (slot !== null) {
      if (!repository && CODE_TOOLS.includes(slot)) return [];
      if (slot === 'terminal' && !shell) return [];
      if (slot === 'desktop' && !cloud) return [];
      if (slot === 'ports' && !cloud && !localPorts) return [];
      if (slot === 'computer' && (!localComputer || cloud)) return [];
      const multiple = item.multiple === true;
      return [
        {
          ...base,
          label: item.label,
          // A browser tab picks its session (shared, isolated, private) from its own toolbar.
          newInstance: multiple && (open || slot === 'browser'),
          group: 'tool',
        },
      ];
    }
    if (item.id.startsWith('plugin:') && !item.id.startsWith(`plugin:${CORE_PANEL_OWNER}/`))
      return [{ ...base, label: item.label, newInstance: false, group: 'app', app: { open } }];
    // The other views: a multi-instance one adds an instance, a single one is offered closed.
    if (item.multiple === true)
      return [
        {
          ...base,
          label: open ? (item.newInstanceLabel ?? `New ${item.label.toLowerCase()}`) : item.label,
          newInstance: open,
          group: 'more',
        },
      ];
    const launchable =
      item.id.startsWith('plugin:') ||
      item.id === 'results' ||
      item.id === 'chat' ||
      (item.id === 'cloud-browser' && cloud);
    if (!launchable || open) return [];
    return [{ ...base, label: item.label, newInstance: false, group: 'more' }];
  });
  const rank = (entry: PanelToolEntry) => {
    const slot = slotOf(entry.item);
    return slot === null ? TOOL_ORDER.length : TOOL_ORDER.indexOf(slot);
  };
  return [
    ...entries.filter((entry) => entry.group === 'tool').sort((a, b) => rank(a) - rank(b)),
    ...entries.filter((entry) => entry.group === 'app'),
    ...entries.filter((entry) => entry.group === 'more'),
  ];
}

/** The entries whose name or purpose matches what is typed in the launcher's search. */
export function matchingToolEntries(
  entries: readonly PanelToolEntry[],
  query: string,
): readonly PanelToolEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return entries.filter((entry) => entry.group !== 'more');
  return entries.filter((entry) =>
    `${entry.label} ${entry.item.label} ${entry.item.description ?? ''}`
      .toLowerCase()
      .includes(needle),
  );
}
